/**
 * @description 腾讯视频Webview适配层实现
 * @author aprilcai
 */

import AdapterBridgeAbstractClass, { defaultInterfaceResult } from '../interface';
import { parse } from '@iv/shared/src/querystring';

import * as commonMethod from './platform/common-method';
import * as phoneMethod from './platform/phone-method';
import * as padMethod from './platform/pad-method';
import * as pcMethod from './platform/pc-method';
import * as webMethod from './platform/web-method';

import { version } from './module/entity';

let methods: { [key: string]: Function } = { ...commonMethod };

const toJsonString = (param: any) => {
  try {
    return JSON.stringify(param);
  } catch (e) {
    return param;
  }
};

/**
 * 遍历各个端的方法，对支持性做判断
 */
[phoneMethod, pcMethod, padMethod, webMethod]
  .filter(platformMethod => platformMethod.support())
  .forEach((platformMethod) => {
    methods = { ...methods, ...platformMethod };
  });

// 不需要上报到sdk流水的方法
const isNotReportSdkFlow = (method: string, param: any) => (method === 'writeLog' && (!(param?.isReportServer)));

/**
 * 上报空结果的方法
 * 部分api的返回结果长度太长了，上报请求会因为请求包体太大而失败，因此上报空的结果
 * 部分api的返回结果存在敏感信息，这种也可以上报空结果
 */
const reportEmptyResultMethods = ['getImageBase64String'];

// 统一包装方法调用，方法调用完之后上报流水日志
/** @this TxvBridge */
async function wrapMethod(
  method: any,
  param = {
    service: '',
    data: {},
  },
): Promise<IResult> {
  const { reportSdkFlow } = this;
  const start = new Date().getTime();
  let res;

  if (typeof methods[method] !== 'undefined') {
    res = await methods[method](param);
  }

  res = res || defaultInterfaceResult;

  if (res.code !== 0) {
    window.console.log('检测到非0返回码。method:', method, ', param:', param, ', res:', res);
  }

  if (!isNotReportSdkFlow(method, param)) {
    // 上报流水日志
    reportSdkFlow({
      api_method: method === 'requestService' ? `requestService.${param.service}` : method,
      api_param: toJsonString(param?.data || param),
      api_code: res.code,
      api_result: reportEmptyResultMethods.includes(method) ? '' : toJsonString(res),
      api_type: 'bridge',
      api_cost_time: new Date().getTime() - start,
      sdk_version: version,
    });
  }

  return Promise.resolve(res);
}

class TxvBridge extends AdapterBridgeAbstractClass {
  // 应用ID
  private appId = 'tiv30io1412ncgtqyu'; // 默认appId，后续需要依赖接口来注入appId
  private h5Shown: boolean; // 安卓视频app下showH5不能重复调用，需要变量控制
  private cid: string; // cid
  private containerId: string; // iframe容器id

  public constructor(options = { appId: '', cid: '', containerId: '' }) {
    super();
    const { appId, cid, containerId } = options;
    if (appId) {
      this.appId = appId;
    }
    this.h5Shown = false;
    const urlParam = parse();
    this.cid = cid || urlParam.cid || '';
    this.containerId = containerId || '';
  }

  // 事件监听
  public on(event: string, callback: Function, param?: any) {
    methods.on(event, callback, param);
  }

  // 取消监听
  public off(event: string, callback: Function, param?: any) {
    methods.off(event, callback, param);
  }

  // 监听一次
  public once(event: string, callback: Function) {
    methods.once(event, callback);
  }

  public compareAppVer(param = { v1: '', v2: '' }): Promise<IResult> {
    return wrapMethod.apply(this, ['compareAppVer', param]);
  }

  // 获取SDK版本
  public getVersion(): Promise<IResult> {
    return wrapMethod.apply(this, ['getVersion']);
  }

  // 获取可用api
  public getAvailableApi(): Promise<IResult> {
    const start = new Date().getTime();
    const result = {
      code: 0,
      msg: 'ok',
      result: Object.keys(methods)
        .filter(m => !/^\$/.test(m)) // 过滤$开头的内部方法
        .sort(),
    };
    // 上报流水日志
    this.reportSdkFlow({
      api_method: 'getAvailableApi',
      api_param: {},
      api_code: 0,
      api_result: JSON.stringify(result),
      api_type: 'bridge',
      api_cost_time: new Date().getTime() - start,
      sdk_version: version,
    });
    return Promise.resolve(result);
  }

  // 获取当前平台
  public getPlatform(): Promise<IResult> {
    return wrapMethod.apply(this, ['getPlatform']);
  }

  // 登录
  public login(): Promise<IResult> {
    return wrapMethod.apply(this, ['login']);
  }

  // 登出
  public logout(): Promise<IResult> {
    return wrapMethod.apply(this, ['logout']);
  }

  // 是否登录
  public isLogin(): Promise<IResult> {
    return wrapMethod.apply(this, ['isLogin']);
  }

  public setServerEnv(param = { isTest: true }): Promise<IResult> {
    return wrapMethod.apply(this, ['setServerEnv', param]);
  }

  // 获取用户信息
  public getUserInfo(param = { isTest: false }): Promise<IResult> {
    return wrapMethod.apply(this, [
      'getUserInfo',
      {
        data: {
          iv_appid: this.appId,
        },
        isTest: param.isTest,
      },
    ]);
  }

  // 获取设备id
  public getDeviceId(): Promise<IResult> {
    return wrapMethod.apply(this, ['getDeviceId']);
  }

  // 获取用户凭据code
  public getAuthCode(param = { isTest: false }): Promise<IResult> {
    return wrapMethod.apply(this, [
      'requestService',
      {
        service: 'getAuthCode',
        data: {
          appid: this.appId,
        },
        isTest: param.isTest,
      },
    ]);
  }

  // 刷新互动登录态票据
  public refreshToken(param = { openId: '', isTest: false }): Promise<IResult> {
    return wrapMethod.apply(this, [
      'requestService',
      {
        service: 'refreshToken',
        data: {
          iv_openid: param.openId,
          iv_appid: this.appId,
        },
        isTest: param.isTest,
      },
    ]);
  }

  // 获取/保存/更新数据到服务端
  public accessStore(param = {
    type: '',
    key: '',
    value: '',
    isTest: false,
  }): Promise<IResult> {
    return wrapMethod.apply(this, [
      'requestService',
      {
        service: 'accessStore',
        data: {
          appid: this.appId,
          op_type: param.type,
          key_word: param.key,
          text_value: param.value,
        },
        isTest: param.isTest,
      },
    ]);
  }

  // 获取/设置本地数据
  public accessLocal(param = {
    type: '',
    key: '',
    value: '',
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['accessLocal', param]);
  }

  // 复制文本内容
  public copyToClipBoard(param = { content: '' }): Promise<IResult> {
    return wrapMethod.apply(this, ['copyToClipBoard', param]);
  }

  // 获取图片url的base64字符串
  public getImageBase64String(param = {
    url: '',
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['getImageBase64String', param]);
  }

  // 添加日历提醒
  public addCalendarNotice(param = {
    title: '',
    startTime: '',
    location: '',
    allDay: 0,
    alarmStr: '',
    notes: '',
    url: '',
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['addCalendarNotice', param]);
  }

  // 添加桌面快捷方式
  public addShortcut(param = {
    title: '',
    icon: '',
    url: '',
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['addShortcut', param]);
  }

  // 在视频App里面收藏/取消收藏
  public collectAction(param = {
    type: '',
  }): Promise<IResult> {
    const p = {
      type: Number(param.type),
      cid: this.cid,
    };
    return wrapMethod.apply(this, ['collectAction', p]);
  }

  // 关掉当前WebView
  public close(): Promise<IResult> {
    return wrapMethod.apply(this, ['close']);
  }

  // 获取屏幕亮度
  public getBrightness(): Promise<IResult> {
    return wrapMethod.apply(this, ['getBrightness']);
  }

  // 设置屏幕亮度
  public setBrightness(param = {
    brightness: 0.5,
  }): Promise<IResult> {
    return wrapMethod.apply(this, [
      'setBrightness',
      {
        brightness: Number(param.brightness),
      },
    ]);
  }

  // 获取系统音量大小
  public getVolume(): Promise<IResult> {
    return wrapMethod.apply(this, ['getVolume']);
  }

  // 设置系统音量大小
  public setVolume(param = {
    volume: 0.5,
  }): Promise<IResult> {
    return wrapMethod.apply(this, [
      'setVolume',
      {
        volume: Number(param.volume),
      },
    ]);
  }

  // 获取网络状态
  public getNetworkState(): Promise<IResult> {
    return wrapMethod.apply(this, ['getNetworkState']);
  }

  // 新打开webview
  public openWebview(param = {
    url: '', // url地址
    close: 1, // 是否关闭当前webview。1: 关闭, 0: 不关闭。默认：0
    style: 1, // 新开webview的样式: 0: 导航显示更多按钮, 1: 导航不显示更多按钮, 2: 关闭loading（仅android支持）。默认：0
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['openWebview', param]);
  }

  public openPayVipView(param = {
    actid: '',
    nt: '',
    n: '',
    sp: '',
    ptag: '',
    aid: '',
    dsp: '',
    tab: '',
    dnp: '',
    unwx: '',
    url: '',
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['openPayVipView', param]);
  }

  // 打开支付页
  public openPayPage(param = {
    appid: '',
    actid: '',
    orderid: '',
    needpay: 0,
    close: 0,
    ru: '',
    title: '',
    sandbox: 0,
    autopay: 1,
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['openPayPage', { ...param, ...{ appid: this.appId } }]);
  }

  // 刷新webview页面标题
  public refreshTitle(param = {
    title: '',
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['refreshTitle', param]);
  }

  // 滚动到某个位置
  public scrollTo(param = {
    top: 0,
    left: 0,
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['scrollTo', param]);
  }

  // 扫描二维码获取二维码信息
  public scanQRCode(): Promise<IResult> {
    return wrapMethod.apply(this, ['scanQRCode']);
  }

  // 旋转屏幕
  public setRequestedOrientation(param = {
    orientation: 0,
  }): Promise<IResult> {
    return wrapMethod.apply(this, [
      'setRequestedOrientation',
      {
        orientation: Number(param.orientation),
      },
    ]);
  }

  // 震动设备
  public shakeDevice(param = {
    type: 'short',
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['shakeDevice', param]);
  }

  // 分享
  public share(param = {
    title: '',
    subTitle: '',
    url: '',
    imageUrl: '',
    sharePageTitle: '',
    sharePageLaunchUrl: '',
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['share', param]);
  }

  // 分享图片
  public shareImage(param = {
    needPreview: false,
    imageUrl: '',
    localImageUrl: '',
    title: '',
    subTitle: '',
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['shareImage', param]);
  }

  // 保存图片
  public saveImage(param = {
    url: '',
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['saveImage', param]);
  }

  // 隐藏/显示左上角系统返回按钮
  public toggleBackButton(param = {
    hide: true,
  }): Promise<IResult> {
    return wrapMethod.apply(this, [
      'toggleBackButton',
      {
        hide: Number(param.hide),
      },
    ]);
  }

  // 写日志到App本地 方便定位问题
  public writeLog(param = {
    content: '',
    isReportServer: false,
    isConsoleLog: true,
  }): Promise<IResult> {
    return wrapMethod.apply(this, [
      'writeLog',
      {
        content: param.content,
        isReportServer: param.isReportServer,
        isConsoleLog: param.isConsoleLog,
        appid: this.appId,
      },
    ]);
  }

  // 上报接口
  public report(param: { type?: string; data?: any }): Promise<IResult> {
    const { type, data } = param;
    const reportParam = {
      type,
      data: {} as any,
    };
    // 兼容旧互动视频
    if (typeof data === 'undefined') {
      reportParam.data = param;
    } else {
      reportParam.data = data;
    }
    if (!reportParam.data.appid) {
      reportParam.data.appid = this.appId;
    }
    if (!reportParam.data.cid) {
      reportParam.data.cid = this.cid;
    }
    return wrapMethod.apply(this, ['report', reportParam]);
  }

  // 请求后台接口服务
  public requestService(param: {
    service: string;
    param?: any;
    isTest?: boolean;
  }): Promise<IResult> {
    return wrapMethod.apply(this, [
      'requestService',
      {
        service: param.service,
        data: { ...param.param, appid: this.appId, isTest: param.isTest },
      },
    ]);
  }

  // toast提示
  public toast(content: string): Promise<IResult> {
    return wrapMethod.apply(this, ['toast', content]);
  }

  // 执行v数值表达式
  public async excuteVExpression(expressionInfo: {
    nodeId: string;
    time?: number;
    expression: string;
  }): Promise<IResult> {
    const { nodeId, time, expression } = expressionInfo;
    return wrapMethod.apply(this, [
      'requestService',
      {
        service: 'updateCurrentNumbers',
        data: {
          branch_id: nodeId,
          time,
          expressions: expression,
        },
      },
    ]);
  }

  // 读取kv值
  public async getString(keys: Array<string>, dramaId: string): Promise<IResult> {
    return wrapMethod.apply(this, ['getString', { keys, dramaId }]);
  }

  // 存储kv值
  public async setString(kv: { key: string; value: string }, dramaId: string): Promise<IResult> {
    return wrapMethod.apply(this, ['setString', { kv, dramaId }]);
  }

  // 加载音频
  public loadSound(soundInfo:
  | {
    id: string;
    src: string;
  }
  | string): Promise<IResult> {
    return wrapMethod.apply(this, ['loadSound', soundInfo]);
  }

  // 播放音频
  public playSound(playInfo: { id: string; loop: boolean; time: number }): Promise<IResult> {
    return wrapMethod.apply(this, ['playSound', playInfo]);
  }

  // 暂停音频
  public stopSound(id: string): Promise<IResult> {
    return wrapMethod.apply(this, ['stopSound', id]);
  }

  // 销毁音频
  public destroySound(id: string): Promise<IResult> {
    return wrapMethod.apply(this, ['destroySound', id]);
  }

  // 销毁全部音频
  public destroyAllSound(): Promise<IResult> {
    return wrapMethod.apply(this, ['destroyAllSound']);
  }

  // 展示互动view
  public showInteract(forceShow?: boolean): Promise<IResult> {
    if (!forceShow && this.h5Shown) return super.showInteract();
    this.h5Shown = true;
    return wrapMethod.apply(this, ['showH5']);
  }

  // 隐藏互动view
  public hideInteract(): Promise<IResult> {
    this.h5Shown = false;
    return wrapMethod.apply(this, ['hideH5']);
  }

  // 重新加载页面
  public reload(isForce?: boolean): Promise<IResult> {
    return wrapMethod.apply(this, ['reload', isForce]);
  }

  // 设置屏幕休眠
  public setScreenSleep(param = { noSleep: true, isForce: false }): Promise<IResult> {
    return wrapMethod.apply(this, ['setScreenSleep', param]);
  }

  // 拉起第三方应用
  public launch3rdApp(param = { pkgName: '', pkgUrl: '' }): Promise<IResult> {
    return wrapMethod.apply(this, ['launch3rdApp', param]);
  }

  // 拉取道具列表
  public async getProductList(param: {
    dramaId: string,
    vid?: string,
    isTest?: boolean,
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['getProductList', param]);
  }

  // 单点支付
  public async pay(param: {
    dramaId: string,
    productId: string,
    isTest?: boolean
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['pay', param]);
  }

  // 查询支付状态
  public getPayState(param: {
    dramaId: string,
    productId?: string,
    isTest?: boolean
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['getPayState', param]);
  }

  // 游戏预约
  public orderGame(param = { type: '', giftId: '', gameId: '', gameName: '', gameIcon: '' }): Promise<IResult> {
    return wrapMethod.apply(this, ['orderGame', param]);
  }

  // 游戏下载
  public downloadGame(param: {
    gameInfo: {
      gameId: string,
      qqGameAppId: string,
      android: object,
      ios: object,
    },
    extra: {
      reportParams: string
    }
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['downloadGame', param]);
  }

  // 排行榜
  public getRankList(param: {
    metaId: string,
    rankId: string,
    pageNum?: number,
    pageSize?: number,
    isTest?: boolean
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['getRankList', param]);
  }

  // 展示插播 H5
  public showClip(param: {
    vid: string,
    x: number,
    y: number,
    width: number,
    height: number,
    hideCloseBtn?: boolean,
  }): Promise<IResult> {
    return wrapMethod.apply(this, ['showClip', {
      param,
      containerId: this.containerId,
    }]);
  }

  // 隐藏插播 H5
  public hideClip() {
    return wrapMethod.apply(this, ['hideClip', this.containerId]);
  }
}

export default function (options = {
  appId: '',
  cid: '',
  containerId: '',
}) {
  return new TxvBridge(options);
}
